import { TypeChecker } from '@attain-apps/type-guards'

import * as models from '../../models'

import type { Query } from './query'
import { Relation } from './relation'

/**
 * Indicates what records the {@link Query} should return
 *
 * __NOTE:__ only a {@link Subquery} can use a {@link Relation} as a
 * target. However, since {@link Subquery} should be assignable to
 * {@link Query}, {@link Relation} is accepted here
 */
export type Target =
  | models.ForRecord.Id
  | models.Dimension.Id
  | Relation
  | Target.DateRange.List
// TODO: maybe have a saved query ID as a target? I.e., can reference other
// queries in this query. This might allow for efficiency when having a
// dashboard full of queries based on the same data
export namespace Target {
  export const isTarget: TypeChecker<Target> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.union(
      models.ForRecord.Id.isId,
      models.Dimension.Id.isId,
      Relation.isRelation,
      Target.DateRange.List.isList,
    ),
  )

  /**
   * All targets in this query or any of its subqueries
   */
  export function allIn(query: Query): Target[] {
    const targets = new Set<Target>()
    const queries: Query[] = [query]
    let currentQuery: Query | undefined
    while ((currentQuery = queries.pop())) {
      if (currentQuery.target) {
        targets.add(currentQuery.target)
      }
      if (currentQuery.subqueries) {
        // Exclude nullish values from subqueries, as they are represented as
        // Partial records.
        queries.push(
          ...Object.values(currentQuery.subqueries).filter(
            TypeChecker.isNotNullish.raw,
          ),
        )
      }
    }
    return Array.from(targets)
  }

  // TODO: Maybe should have a limit of two elements
  // e.g. ["2021-01-01", "2021-12-31"]
  export type DateRange = readonly string[]
  export namespace DateRange {
    export const isDateRange = TypeChecker.Factory.list(TypeChecker.isString)

    export type List = readonly DateRange[]
    export namespace List {
      export const isList = TypeChecker.Factory.list(isDateRange)
    }
  }
}
